import { useEffect, useState } from 'react';
import { Icon } from '@iconify/react';
import { Dialog, DialogContent } from '@/components/shadcn/dialog';
import { getBookingCalendar, type CalendarField, type CalendarSlot } from '@/services/bookingCalendarService';
import {
  formatDayTitle,
  formatRupiah,
  formatSelectedSlotRanges,
  isSlotHourPassed,
  parseDateKey,
  toDateKey,
} from '@/components/admin/booking/bookingCalendarUtils';
import type { ManualTarget } from '@/components/admin/booking/useBookingCalendar';
import { cn } from '@/lib/utils';

interface ManualBookingDrawerProps {
  target: ManualTarget | null;
  fields: CalendarField[];
  arenaSlug: string;
  onClose: () => void;
  onSubmit: (payload: {
    team_name: string;
    phone_number: string;
    description?: string;
    field_slug: string;
    booking_date: string;
    selected_slots: string[];
    payment_status: 'paid' | 'dp_paid' | 'unpaid';
  }) => Promise<void>;
  initialCustomerName?: string;
  initialPhone?: string;
}

const PAYMENT_OPTIONS = [
  { id: 'unpaid', label: 'Belum bayar' },
  { id: 'dp_paid', label: 'DP 50%' },
  { id: 'paid', label: 'Lunas' },
] as const;

export default function ManualBookingDrawer({
  target,
  fields,
  arenaSlug,
  onClose,
  onSubmit,
  initialCustomerName,
  initialPhone,
}: ManualBookingDrawerProps) {
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [description, setDescription] = useState('');
  const [fieldSlug, setFieldSlug] = useState('');
  const [date, setDate] = useState('');
  const [selectedSlotIds, setSelectedSlotIds] = useState<string[]>([]);
  const [initializedTargetKey, setInitializedTargetKey] = useState('');
  const [source, setSource] = useState<'walk_in' | 'phone'>('walk_in');
  const [paymentStatus, setPaymentStatus] = useState<'paid' | 'dp_paid' | 'unpaid'>('unpaid');
  const [saving, setSaving] = useState(false);
  const [daySlots, setDaySlots] = useState<CalendarSlot[]>([]);

  const currentTargetKey = target ? `${target.date}_${target.fieldSlug}_${target.start}_${target.end}` : '';

  useEffect(() => {
    if (!target) return;
    setName(initialCustomerName || '');
    setPhone(initialPhone || '');
    setDescription('');
    setFieldSlug(target.fieldSlug || (fields[0]?.slug ?? ''));
    setDate(target.date || toDateKey(new Date()));
    setSelectedSlotIds([]);
    setInitializedTargetKey('');
    setSource('walk_in');
    setPaymentStatus('unpaid');
  }, [target, initialCustomerName, initialPhone, fields]);

  useEffect(() => {
    if (!arenaSlug || !fieldSlug || !date) return;
    let active = true;
    void getBookingCalendar(arenaSlug, date, date, fieldSlug)
      .then((result) => {
        if (!active) return;
        const slots = result.fields[0]?.slots ?? [];
        setDaySlots(slots);

        // Inisialisasi slot dari target yang diklik pertama kali pada kalender (hanya jika buka dan belum terlewat)
        if (target && currentTargetKey && initializedTargetKey !== currentTargetKey) {
          const match = slots.find((s) => s.start === target.start && s.end === target.end)
            ?? slots.find((s) => s.start === target.start);
          const isPassed = match ? isSlotHourPassed(date, match.end) : false;
          if (match && match.state === 'open' && !isPassed) {
            setSelectedSlotIds([match.public_id]);
          }
          setInitializedTargetKey(currentTargetKey);
        }
      })
      .catch(() => {
        if (active) setDaySlots([]);
      });
    return () => {
      active = false;
    };
  }, [arenaSlug, fieldSlug, date, target, currentTargetKey, initializedTargetKey]);

  const selectedSlots = daySlots
    .filter((slot) => selectedSlotIds.includes(slot.public_id))
    .filter((slot) => slot.state === 'open' && !isSlotHourPassed(date, slot.end))
    .sort((a, b) => a.start.localeCompare(b.start));
  const total = selectedSlots.reduce((sum, slot) => sum + slot.price, 0);

  const selectedField = fields.find((item) => item.slug === fieldSlug);

  // Available unique hours from daySlots
  const availableHourSlots = daySlots.map((s) => ({
    publicId: s.public_id,
    start: s.start,
    end: s.end,
    state: s.state,
    label: s.start.replace(':', '.'),
  }));

  const handleHourClick = (slotPublicId: string, slotState: string, isPast: boolean) => {
    if (slotState !== 'open' || isPast) return;

    setSelectedSlotIds((prev) => {
      if (prev.includes(slotPublicId)) {
        return prev.filter((id) => id !== slotPublicId);
      }
      return [...prev, slotPublicId];
    });
  };

  const save = async () => {
    if (!name.trim() || selectedSlots.length === 0) return;
    setSaving(true);
    try {
      const sourceTag = source === 'walk_in' ? 'Walk-In' : 'Telepon / WA';
      const finalDesc = description.trim() ? `[${sourceTag}] ${description.trim()}` : `[${sourceTag}]`;

      await onSubmit({
        team_name: name.trim(),
        phone_number: phone.trim(),
        description: finalDesc,
        field_slug: fieldSlug,
        booking_date: date,
        selected_slots: selectedSlots.map((s) => s.public_id),
        payment_status: paymentStatus,
      });
    } finally {
      setSaving(false);
    }
  };

  const summaryDate = date ? formatDayTitle(parseDateKey(date)) : '';

  return (
    <Dialog open={Boolean(target)} onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent showCloseButton={false} className="max-w-xl rounded-2xl border border-[#E0E6ED] bg-white p-6 shadow-2xl sm:max-w-xl">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-[#E0E6ED] pb-3">
          <h2 className="text-lg font-semibold text-[#0E1726]">Tambah Booking</h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Tutup"
            className="flex h-8 w-8 items-center justify-center rounded-full text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"
          >
            <Icon icon="ph:x-bold" className="h-4 w-4" />
          </button>
        </div>

        {/* Form Body */}
        <div className="mt-4 space-y-4 max-h-[75vh] overflow-y-auto pr-1">
          {/* Row 1: Nama Pelanggan & No HP */}
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div>
              <label className="block text-xs font-semibold text-[#0E1726] mb-1.5">Nama pelanggan</label>
              <input
                type="text"
                placeholder="Contoh: Garuda Muda FC"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="h-10 w-full rounded-full border border-[#E0E6ED] bg-white px-4 text-sm text-[#0E1726] placeholder:text-gray-400 focus:border-[#FC480D] focus:outline-none"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-[#0E1726] mb-1.5">No. HP</label>
              <input
                type="text"
                placeholder="08xx-xxxx-xxxx"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                className="h-10 w-full rounded-full border border-[#E0E6ED] bg-white px-4 text-sm text-[#0E1726] placeholder:text-gray-400 focus:border-[#FC480D] focus:outline-none"
              />
            </div>
          </div>

          {/* Row 2: Tanggal */}
          <div>
            <label className="block text-xs font-semibold text-[#0E1726] mb-1.5">Tanggal</label>
            <div className="relative">
              <input
                type="date"
                value={date}
                min={toDateKey(new Date())}
                onChange={(e) => {
                  setDate(e.target.value);
                  setSelectedSlotIds([]);
                }}
                className="h-10 w-full rounded-full border border-[#E0E6ED] bg-white px-4 text-sm text-[#0E1726] focus:border-[#FC480D] focus:outline-none"
              />
            </div>
          </div>

          {/* Row 3: Lapangan */}
          <div>
            <label className="block text-xs font-semibold text-[#0E1726] mb-1.5">Lapangan</label>
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
              {fields.map((item) => {
                const isSelected = item.slug === fieldSlug;
                return (
                  <button
                    key={item.slug}
                    type="button"
                    className={cn(
                      'rounded-xl border p-2.5 text-left transition-all',
                      isSelected
                        ? 'border-2 border-[#FC480D] bg-[#FFF5EE]'
                        : 'border-[#E0E6ED] bg-white hover:bg-gray-50'
                    )}
                    onClick={() => {
                      setFieldSlug(item.slug);
                      setSelectedSlotIds([]);
                    }}
                  >
                    <p className={cn('text-xs font-semibold truncate', isSelected ? 'text-[#FC480D]' : 'text-[#0E1726]')}>
                      {item.name}
                    </p>
                    <p className="truncate text-[11px] text-gray-500">{item.sport_name || 'Vinyl · Indoor'}</p>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Row 4: Jam (bisa pilih lebih dari satu) */}
          <div>
            <label className="block text-xs font-semibold text-[#0E1726] mb-1.5">
              Jam · bisa pilih lebih dari satu
            </label>
            {availableHourSlots.length > 0 ? (
              <div className="flex flex-wrap gap-2">
                {availableHourSlots.map((slot) => {
                  const isSelected = selectedSlotIds.includes(slot.publicId);
                  const isPast = isSlotHourPassed(date, slot.end);
                  const isBooked = slot.state === 'booked';
                  const isLocked = slot.state === 'locked';
                  const isBlocked = slot.state === 'blocked';
                  const isUnavailable = slot.state !== 'open' || isPast;

                  const badgeTitle = isPast
                    ? 'Jam telah terlewat'
                    : isBooked
                    ? 'Sudah dibooking'
                    : isLocked
                    ? 'Lapangan tutup'
                    : isBlocked
                    ? 'Sesi ditutup (Custom)'
                    : undefined;

                  return (
                    <button
                      key={slot.publicId}
                      type="button"
                      disabled={isUnavailable}
                      title={badgeTitle}
                      className={cn(
                        'rounded-full px-3 py-1.5 text-xs font-semibold transition-all',
                        isUnavailable
                          ? 'border border-gray-200 bg-gray-100 text-gray-400 cursor-not-allowed opacity-60'
                          : isSelected
                          ? 'border border-[#FC480D] bg-[#FFF5EE] text-[#FC480D] shadow-xs'
                          : 'border border-[#E0E6ED] bg-white text-gray-700 hover:border-gray-400'
                      )}
                      onClick={() => handleHourClick(slot.publicId, slot.state, isPast)}
                    >
                      {slot.label}
                    </button>
                  );
                })}
              </div>
            ) : (
              <p className="text-xs text-gray-400 italic">Memuat jadwal jam...</p>
            )}
          </div>

          {/* Row 5: Sumber & Pembayaran */}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label className="block text-xs font-semibold text-[#0E1726] mb-1.5">Sumber</label>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  className={cn(
                    'flex-1 rounded-full border py-2 text-xs font-semibold transition-all',
                    source === 'walk_in'
                      ? 'border-[#FC480D] bg-[#FFF5EE] text-[#FC480D]'
                      : 'border-[#E0E6ED] bg-white text-gray-700 hover:bg-gray-50'
                  )}
                  onClick={() => setSource('walk_in')}
                >
                  Walk-In
                </button>
                <button
                  type="button"
                  className={cn(
                    'flex-1 rounded-full border py-2 text-xs font-semibold transition-all',
                    source === 'phone'
                      ? 'border-[#FC480D] bg-[#FFF5EE] text-[#FC480D]'
                      : 'border-[#E0E6ED] bg-white text-gray-700 hover:bg-gray-50'
                  )}
                  onClick={() => setSource('phone')}
                >
                  Telepon / WA
                </button>
              </div>
            </div>

            <div>
              <label className="block text-xs font-semibold text-[#0E1726] mb-1.5">Pembayaran</label>
              <div className="flex items-center gap-1.5">
                {PAYMENT_OPTIONS.map((opt) => {
                  const isSelected = paymentStatus === opt.id;
                  return (
                    <button
                      key={opt.id}
                      type="button"
                      className={cn(
                        'flex-1 rounded-full border py-2 text-xs font-semibold transition-all text-center',
                        isSelected
                          ? 'border-[#FC480D] bg-[#FFF5EE] text-[#FC480D]'
                          : 'border-[#E0E6ED] bg-white text-gray-700 hover:bg-gray-50'
                      )}
                      onClick={() => setPaymentStatus(opt.id)}
                    >
                      {opt.label}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Summary Box */}
          <div className="flex flex-col gap-1 rounded-xl bg-[#F5F6F7] p-3 text-xs sm:flex-row sm:items-center sm:justify-between">
            <div className="min-w-0">
              <span className="text-gray-500">Jadwal: </span>
              <span className="font-semibold text-[#0E1726]">
                {summaryDate} · {selectedField?.name || 'Lapangan'} · {formatSelectedSlotRanges(selectedSlots)}
              </span>
            </div>
            <div className="shrink-0">
              <span className="text-gray-500">Total: </span>
              <span className="text-sm font-semibold text-[#FC480D]">{formatRupiah(total)}</span>
            </div>
          </div>
        </div>

        {/* Footer Actions */}
        <div className="mt-5 flex items-center justify-end gap-2 border-t border-[#E0E6ED] pt-3">
          <button
            type="button"
            className="h-10 rounded-full border border-[#E0E6ED] bg-white px-6 text-sm font-semibold text-gray-700 transition-colors hover:bg-gray-50"
            onClick={onClose}
          >
            Batal
          </button>
          <button
            type="button"
            disabled={saving || !name.trim() || selectedSlots.length === 0}
            className="h-10 rounded-full bg-[#FC480D] px-6 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-[#E8490F] disabled:cursor-not-allowed disabled:opacity-50"
            onClick={() => {
              void save();
            }}
          >
            {saving ? 'Menyimpan...' : 'Simpan Booking'}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
